import { ChangeEvent, DragEvent, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
  alpha
} from "@mui/material";
import Iconify from "../../../minimals/components/iconify";
import { OrderDocumentCategory } from "../types";

const getBase64File = (file: File) =>
  new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      let encoded = result.replace(/^data:(.*;base64,)?/, "");
      if (encoded.length % 4 > 0) {
        encoded += "=".repeat(4 - (encoded.length % 4));
      }
      resolve(encoded);
    };
    reader.readAsDataURL(file);
  });

const wait = (milliseconds: number) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

export function UploadDrawer({
  open,
  orderNumber,
  initialFile,
  onUploaded,
  onClose
}: {
  open: boolean;
  orderNumber: string;
  initialFile?: File | null;
  onUploaded: () => Promise<void>;
  onClose: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [categoryOptions, setCategoryOptions] = useState<OrderDocumentCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitClicked, setSubmitClicked] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isFileDragging, setIsFileDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [data, setData] = useState({
    mtaNum: orderNumber,
    textToLink: "",
    category: "",
    subcategoryId: "",
    subcategory: "",
    description: "",
    additionalDescription: ""
  });

  const selectedCategory = categoryOptions.find((category) => category.name === data.category);
  const selectedSubcategory = categoryOptions
    .flatMap((category) => category.subcategories)
    .find((subcategory) => subcategory.id === data.subcategoryId);
  const descriptionOptions = selectedSubcategory?.descriptions?.filter(Boolean) ?? [];
  const quickSearchItems = useMemo(
    () =>
      categoryOptions.flatMap((category) =>
        category.subcategories.flatMap((subcategory) =>
          (subcategory.descriptions ?? []).filter(Boolean).map((description) => ({
            label: `${category.name} > ${subcategory.name} > ${description}`,
            category: category.name,
            subcategory: subcategory.name,
            subcategoryId: subcategory.id,
            description
          }))
        )
      ),
    [categoryOptions]
  );
  const selectedQuickSearchItem =
    quickSearchItems.find(
      (item) =>
        item.category === data.category &&
        item.subcategoryId === data.subcategoryId &&
        item.description === data.description
    ) ?? null;
  const errors = {
    mtaNum: submitClicked && !data.mtaNum,
    category: submitClicked && !data.category,
    subcategoryId: submitClicked && !data.subcategoryId,
    description: submitClicked && !data.description,
    additionalDescription: submitClicked && data.description === "Other" && !data.additionalDescription,
    file: submitClicked && !file
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    setData({
      mtaNum: orderNumber,
      textToLink: "",
      category: "",
      subcategoryId: "",
      subcategory: "",
      description: "",
      additionalDescription: ""
    });
    setFile(initialFile ?? null);
    setSubmitClicked(false);
    setUploadError(null);

    const loadCategories = async () => {
      setCategoriesLoading(true);
      try {
        const { data: nextCategories } = await axios.get<OrderDocumentCategory[]>(
          `/api/orderdocuments/categories?orderNumber=${encodeURIComponent(orderNumber)}`
        );
        setCategoryOptions(nextCategories ?? []);
      } catch {
        setCategoryOptions([]);
        setUploadError("Categories could not be loaded.");
      } finally {
        setCategoriesLoading(false);
      }
    };

    loadCategories();
  }, [initialFile, open, orderNumber]);

  const hasValidationErrors = () =>
    !data.mtaNum ||
    !data.category ||
    !data.subcategoryId ||
    !data.description ||
    (data.description === "Other" && !data.additionalDescription) ||
    !file;

  const selectUploadFile = (nextFile?: File | null) => {
    if (!nextFile) {
      return;
    }

    setFile(nextFile);
    setSubmitClicked(false);
    setUploadError(null);
  };

  const handleFileDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "copy";
    setIsFileDragging(true);
  };

  const handleFileDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
      return;
    }

    setIsFileDragging(false);
  };

  const handleFileDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsFileDragging(false);
    selectUploadFile(event.dataTransfer.files?.[0]);
  };

  const submitUpload = async () => {
    setSubmitClicked(true);
    setUploadError(null);
    if (hasValidationErrors() || !file) {
      return;
    }

    setIsSubmitting(true);
    try {
      let description = data.description;
      if (description === "Other") {
        description = data.additionalDescription;
      } else if (data.additionalDescription) {
        description += ` ${data.additionalDescription}`;
      }

      await axios.post<{ documentId: number }>("/api/orderdocuments/upload", {
        document: {
          mtaNum: data.mtaNum,
          textToLink: data.textToLink,
          category: data.category,
          subcategoryId: data.subcategoryId,
          documentDescription: description,
          saveToWeb: false
        },
        base64File: await getBase64File(file),
        fileName: file.name
      });

      await onUploaded();
      await wait(1000);
      await onUploaded();
      onClose();
    } catch (error) {
      setUploadError(
        axios.isAxiosError(error) && error.response?.data
          ? `Document could not be uploaded. ${String(error.response.data)}`
          : "Document could not be uploaded."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 520 },
          maxWidth: "100%"
        }
      }}
    >
      <Stack sx={{ height: "100%" }}>
        <Box
          sx={{
            px: 2,
            py: 1.25,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid",
            borderColor: "divider"
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              Upload
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {orderNumber}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </Box>

        <Stack spacing={2} sx={{ flex: 1, overflow: "auto", p: 2 }}>
          {uploadError && <Alert severity="error">{uploadError}</Alert>}
          {categoriesLoading ? (
            <Stack spacing={1.25}>
              <Skeleton variant="rounded" height={54} />
              <Skeleton variant="rounded" height={54} />
              <Skeleton variant="rounded" height={54} />
            </Stack>
          ) : (
            <>
              <TextField size="small"
                label="MTA#"
                value={data.mtaNum}
                error={errors.mtaNum}
                onChange={(event) => setData({ ...data, mtaNum: event.target.value })}
              />
              <TextField size="small"
                label="Text to Link"
                value={data.textToLink}
                onChange={(event) => setData({ ...data, textToLink: event.target.value })}
              />
              <Autocomplete
                options={quickSearchItems}
                value={selectedQuickSearchItem}
                getOptionLabel={(option) => option.label}
                onChange={(_, value) => {
                  if (!value) {
                    return;
                  }

                  setData({
                    ...data,
                    category: value.category,
                    subcategoryId: value.subcategoryId,
                    subcategory: value.subcategory,
                    description: value.description
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="QuickSearch of Category > Subcategory > Description"
                    placeholder="Select..."
                    error={errors.category || errors.subcategoryId || errors.description}
                  />
                )}
              />
              <TextField size="small"
                select
                label="Category"
                value={data.category}
                error={errors.category}
                onChange={(event) =>
                  setData({
                    ...data,
                    category: event.target.value,
                    subcategoryId: "",
                    subcategory: "",
                    description: ""
                  })
                }
              >
                {categoryOptions.map((category) => (
                  <MenuItem key={category.name} value={category.name}>
                    {category.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField size="small"
                select
                label="Subcategory"
                value={data.subcategoryId}
                error={errors.subcategoryId}
                disabled={!selectedCategory}
                onChange={(event) => {
                  const subcategory = selectedCategory?.subcategories.find((option) => option.id === event.target.value);
                  setData({
                    ...data,
                    subcategoryId: event.target.value,
                    subcategory: subcategory?.name ?? "",
                    description: ""
                  });
                }}
              >
                {(selectedCategory?.subcategories ?? []).map((subcategory) => (
                  <MenuItem key={subcategory.id} value={subcategory.id}>
                    {subcategory.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField size="small"
                select
                label="Description"
                value={data.description}
                error={errors.description}
                disabled={!selectedSubcategory}
                onChange={(event) => setData({ ...data, description: event.target.value })}
              >
                {[...descriptionOptions, "Other"].map((description) => (
                  <MenuItem key={description} value={description}>
                    {description}
                  </MenuItem>
                ))}
              </TextField>
              <TextField size="small"
                label="Additional Description"
                value={data.additionalDescription}
                error={errors.additionalDescription}
                onChange={(event) => setData({ ...data, additionalDescription: event.target.value })}
              />
              <Paper
                variant="outlined"
                onDragOver={handleFileDragOver}
                onDragEnter={handleFileDragOver}
                onDragLeave={handleFileDragLeave}
                onDrop={handleFileDrop}
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  borderStyle: "dashed",
                  borderColor: errors.file ? "error.main" : isFileDragging ? "primary.main" : "divider",
                  backgroundColor: isFileDragging ? alpha("#1a1aaa", 0.06) : "transparent",
                  transition: "border-color 120ms ease, background-color 120ms ease"
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<Iconify icon="eva:cloud-upload-fill" />}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      borderRadius: 1,
                      fontWeight: 700,
                      textTransform: "none",
                      whiteSpace: "nowrap",
                      flexShrink: 0
                    }}
                  >
                    Choose file
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    {file ? file.name : isFileDragging ? "drop here" : "or drag and drop file"}
                  </Typography>
                </Stack>
                <input
                  ref={fileInputRef}
                  hidden
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={(event: ChangeEvent<HTMLInputElement>) => selectUploadFile(event.target.files?.[0])}
                />
              </Paper>
            </>
          )}
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}
        >
          <Button size="small" variant="text" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={submitUpload} disabled={isSubmitting || categoriesLoading}>
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
}
